/*
 * <notify-me> — back-in-stock email collector for sold-out PDP
 * variants. Sits in the slot below the Add to Cart button, sharing
 * it with the dynamic checkout buttons (Buy It Now / Shop Pay);
 * visibility is mutually exclusive with the checkout slot - this
 * element shows when the variant is sold out, the checkout slot
 * shows otherwise. The Add to Cart button itself stays visible
 * either way (becomes a disabled "Sold out" label when unavailable).
 *
 * Why we don't subscribe to PUB_SUB_EVENTS.variantChange: those
 * symbols live at the top of t-product.js as file-scoped const, not
 * on `window`, so any separate module would have to either modify
 * that file or risk breaking on future minification. Instead we
 * observe product-info (the stable parent that doesn't get swapped
 * on variant change) and re-read the [data-selected-variant] script
 * fresh - same pattern proven by c-pdp-instore.js.
 *
 * Klaviyo client BIS endpoint:
 *   POST https://a.klaviyo.com/client/back-in-stock-subscriptions/
 * Success is HTTP 202 with EMPTY body - DO NOT call res.json().
 *
 * Variant id format (critical, undocumented gotcha):
 *   `$shopify:::$default:::<numeric_variant_id>`
 * Wrong format silently never triggers the Klaviyo flow.
 */

(() => {
    'use strict'

    const KLAVIYO_REVISION = '2024-10-15'
    const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/'
    const EMAIL_RE = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i

    const safeJsonParse = (str) => {
        if (!str) return null
        try { return JSON.parse(str) } catch { return null }
    }

    class NotifyMe extends HTMLElement {
        connectedCallback() {
            if (this._mounted) return
            this._mounted = true

            this.sectionId = this.dataset.sectionId
            if (!this.sectionId) return

            this.productInfo = document.getElementById(
                `MainProduct-${this.sectionId}`,
            )
            if (!this.productInfo) return

            this.trigger = this.querySelector('[data-notify-trigger]')
            this.panel = this.querySelector('[data-notify-panel]')
            this.input = this.querySelector('[data-notify-input]')
            this.submitBtn = this.querySelector('[data-notify-submit]')
            this.status = this.querySelector('[data-notify-status]')
            if (!this.trigger || !this.panel || !this.input || !this.submitBtn || !this.status) return

            // Variants we've already successfully subscribed to in this
            // session. Locks the submit so a customer can't accidentally
            // re-fire the same subscription for the same SKU.
            this.lockedVariants = new Set()

            // Trigger click → reveal the email panel inline below the
            // button. Clicking again collapses it (lightweight toggle,
            // no slide animation - the flex column gap absorbs the
            // size change cleanly).
            this.trigger.addEventListener('click', () => this._togglePanel())

            // <notify-me> sits inside the outer PDP <form>, so its
            // submission control is a <button type="button"> not a
            // <form>. Submit fires from click on the submit button or
            // Enter on the input; both call _onSubmit, and Enter
            // stops propagation so the outer Add-to-Cart form doesn't
            // catch it as its own submit.
            this.submitBtn.addEventListener('click', (e) => this._onSubmit(e))
            this.input.addEventListener('keydown', (e) => {
                if (e.key !== 'Enter') return
                e.preventDefault()
                e.stopPropagation()
                this._onSubmit(e)
            })

            // React to variant changes via the same mechanism as
            // c-pdp-instore.js. product-info is the stable wrapper;
            // variant-selects inside it gets swapped wholesale.
            this._observer = new MutationObserver(() => this._sync())
            this._observer.observe(this.productInfo, {
                childList: true,
                subtree: true,
                characterData: true,
            })

            // First sync. Initial visibility came from Liquid (server-side
            // variant.available), this just brings runtime state in line.
            this._sync()
        }

        disconnectedCallback() {
            this._observer?.disconnect()
        }

        _getVariant() {
            const vs = this.productInfo.querySelector(
                `variant-selects[data-section="${this.sectionId}"]`,
            )
            const script = vs?.querySelector('[data-selected-variant]')
            const parsed = safeJsonParse(script?.textContent)
            if (parsed) return parsed
            // Fallback for products with only the default variant -
            // no <variant-selects> rendered, but the hidden input on the
            // product form still carries the current id.
            const idInput = this.productInfo.querySelector(
                `#product-form-${this.sectionId} input[name="id"]`,
            )
            const idNum = idInput?.value ? Number(idInput.value) : null
            if (!idNum) return null
            // We can't know `available` from the input alone. Conservative:
            // treat as available (button stays visible, notify-me hidden).
            return { id: idNum, available: true, _fallback: true }
        }

        _getCheckoutSlot() {
            return document.getElementById(`DynamicCheckoutButtons-${this.sectionId}`)
        }

        _sync() {
            const variant = this._getVariant()
            const checkoutSlot = this._getCheckoutSlot()
            // States per spec:
            //   variant.available === false       -> notify visible, checkout slot hidden
            //   variant.available === true        -> checkout slot visible, notify hidden
            //   variant null (unavailable combo)  -> checkout slot visible, notify hidden
            // The Add to Cart button always stays visible (becomes a
            // disabled "Sold out" label when unavailable - we do not
            // touch it from here).
            const soldOut = variant?.available === false
            const newVariantId = variant?.id != null ? String(variant.id) : null
            const newLocked = !!(newVariantId && this.lockedVariants.has(newVariantId))

            // BAIL EARLY if nothing observable has changed since the
            // last sync. CRITICAL: this method runs from a
            // MutationObserver attached to product-info with subtree
            // true, and <notify-me> lives inside that subtree - any
            // mutation we make here (panel.hidden, input.disabled,
            // status text/class) re-fires the observer, which re-runs
            // _sync, which mutates again. Without this guard we get
            // an unbounded loop that hangs the tab the moment anyone
            // opens the panel or types in the input.
            if (
                this._initialized &&
                newVariantId === this.currentVariantId &&
                soldOut === this._lastSoldOut &&
                newLocked === this._lastLocked
            ) {
                return
            }

            const variantChanged =
                this._initialized && newVariantId !== this.currentVariantId
            this._initialized = true
            this.currentVariantId = newVariantId
            this._lastSoldOut = soldOut
            this._lastLocked = newLocked

            // Idempotent visibility toggle.
            if (this.hidden !== !soldOut) this.hidden = !soldOut
            if (checkoutSlot && checkoutSlot.hidden !== soldOut) checkoutSlot.hidden = soldOut

            // Collapse only on actual variant switch (not on first sync).
            if (variantChanged) this._collapsePanel()

            // Lock UI mirrors lockedVariants state for the current variant.
            if (newLocked) {
                this._lock()
            } else {
                this._unlock()
            }
        }

        _togglePanel() {
            if (this.panel.hidden) {
                this._expandPanel()
            } else {
                this._collapsePanel()
            }
        }

        _expandPanel() {
            if (this.panel.hidden) this.panel.hidden = false
            if (this.trigger.getAttribute('aria-expanded') !== 'true') {
                this.trigger.setAttribute('aria-expanded', 'true')
            }
            // Auto-focus the input so the customer can type immediately —
            // they just clicked "Notify me" with clear intent.
            requestAnimationFrame(() => this.input.focus())
        }

        _collapsePanel() {
            if (!this.panel.hidden) this.panel.hidden = true
            if (this.trigger.getAttribute('aria-expanded') !== 'false') {
                this.trigger.setAttribute('aria-expanded', 'false')
            }
        }

        _setStatus(message, kind) {
            // Idempotent — every write here is observed by the subtree
            // MutationObserver above, so avoid no-op mutations that
            // would re-fire it unnecessarily.
            const target = message || ''
            const wantsKind = kind || null
            const hasKind = this.status.classList.contains('is-error')
                ? 'error'
                : this.status.classList.contains('is-success')
                  ? 'success'
                  : null
            if (this.status.textContent === target && hasKind === wantsKind) return
            this.status.textContent = target
            this.status.classList.remove('is-error', 'is-success')
            if (wantsKind) this.status.classList.add(`is-${wantsKind}`)
            this.status.hidden = !target
        }

        _lock() {
            if (this.input.disabled !== true) this.input.disabled = true
            if (this.submitBtn.disabled !== true) this.submitBtn.disabled = true
            this._setStatus(window.notifyMeStrings?.success || '', 'success')
        }

        _unlock() {
            if (this.input.disabled !== false) this.input.disabled = false
            if (this.submitBtn.disabled !== false) this.submitBtn.disabled = false
            // Don't wipe an active error - only clear stale success copy.
            if (this.status.classList.contains('is-success')) {
                this._setStatus('', null)
            }
        }

        async _onSubmit(event) {
            event.preventDefault()
            if (!this.currentVariantId) return
            if (this.lockedVariants.has(this.currentVariantId)) return

            const email = (this.input.value || '').trim()
            const strings = window.notifyMeStrings || {}

            if (!EMAIL_RE.test(email)) {
                this._setStatus(strings.invalid_email || 'Invalid email.', 'error')
                this.input.focus()
                return
            }

            const companyId = window.ENRAGE_KLAVIYO_COMPANY_ID
            if (!companyId) {
                console.error('[notify-me] missing window.ENRAGE_KLAVIYO_COMPANY_ID')
                this._setStatus(strings.error || 'Error.', 'error')
                return
            }

            this.submitBtn.disabled = true

            const body = {
                data: {
                    type: 'back-in-stock-subscription',
                    attributes: {
                        channels: ['EMAIL'],
                        profile: {
                            data: {
                                type: 'profile',
                                attributes: { email },
                            },
                        },
                    },
                    relationships: {
                        variant: {
                            data: {
                                type: 'catalog-variant',
                                id: `$shopify:::$default:::${this.currentVariantId}`,
                            },
                        },
                    },
                },
            }

            try {
                const res = await fetch(
                    `${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(companyId)}`,
                    {
                        method: 'POST',
                        headers: {
                            revision: KLAVIYO_REVISION,
                            'content-type': 'application/json',
                            accept: 'application/json',
                        },
                        body: JSON.stringify(body),
                    },
                )
                // Success is 202 with empty body. Do NOT call res.json().
                if (res.ok || res.status === 202) {
                    this.lockedVariants.add(this.currentVariantId)
                    this._lock()
                    return
                }
                throw new Error(`Klaviyo BIS HTTP ${res.status}`)
            } catch (err) {
                console.error('[notify-me]', err)
                this._setStatus(strings.error || 'Error.', 'error')
                this.submitBtn.disabled = false
            }
        }
    }

    if (!customElements.get('notify-me')) {
        customElements.define('notify-me', NotifyMe)
    }
})()
